const SITE_ORIGIN = "https://magnum-fs.com";

const RESIZE_PARAMS = new Set([
  "auto",
  "dpr",
  "fit",
  "fm",
  "format",
  "h",
  "height",
  "q",
  "quality",
  "resize",
  "w",
  "width",
]);

/**
 * Converts a stored product image URL to the original public asset URL.
 * This is intentionally separate from optimizeImage(), which is for rendered
 * UI thumbnails and must not be used in feeds or structured data.
 */
export function originalImageUrl(value: string | null | undefined): string {
  if (!value) return "";

  let url: URL;
  try {
    url = new URL(value, SITE_ORIGIN);
  } catch {
    return "";
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return "";

  // Convert Supabase's image transformation endpoint back to the source object.
  url.pathname = url.pathname.replace(
    "/storage/v1/render/image/public/",
    "/storage/v1/object/public/",
  );

  // Remove dynamic transformation parameters while preserving unrelated URL data.
  for (const key of Array.from(url.searchParams.keys())) {
    if (RESIZE_PARAMS.has(key.toLowerCase())) url.searchParams.delete(key);
  }

  // WordPress-style and common CMS thumbnail suffixes point to smaller variants.
  url.pathname = url.pathname.replace(
    /(?:[-_](?:\d{2,5}x\d{2,5}|thumb(?:nail)?|small|medium|large|scaled))(?=\.[^/]+$)/i,
    "",
  );

  url.hash = "";

  // One canonical, indexable URL per image: the storage host sends
  // `x-robots-tag: none`, so storage objects are served via magnum-fs.com.
  const marker = "/storage/v1/object/public/";
  const idx = url.pathname.indexOf(marker);
  if (idx !== -1) {
    return `${SITE_ORIGIN}/api/public/img/${url.pathname.slice(idx + marker.length)}`;
  }
  if (url.pathname.startsWith("/api/public/img/")) {
    return `${SITE_ORIGIN}${url.pathname}`;
  }
  return url.toString();
}

export function originalImageUrls(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map(originalImageUrl).filter(Boolean)));
}

/**
 * Absolute 1200x630 (1.91:1) share image for og:image / twitter:image.
 * The product photo is scaled to fit and padded on white, so link previews
 * show the whole product instead of a cropped square.
 */
export function socialImageUrl(value: string | null | undefined): string {
  const original = originalImageUrl(value);
  if (!original) return "";
  const marker = "/api/public/img/";
  const idx = original.indexOf(marker);
  if (idx === -1) return original;
  const objectPath = original.slice(idx + marker.length).split("?")[0];
  // Version the renderer so improvements are not hidden by long-lived social/CDN caches.
  return `${SITE_ORIGIN}/api/public/og/${objectPath}?v=2`;
}
